import React, { useEffect, useRef, useState } from "react";
import { faPencilAlt, faPaperclip } from '@fortawesome/fontawesome-free-solid';
import { iNote } from "../../../shared/interfaces";
import C from '../../../shared/constants';
import { useHistory } from "react-router-dom";
import { Fa } from "../../elements/fa";
import styles from './Notepad.module.scss';
import moment from "moment-timezone";
import GalleryModal, { GalleryFile } from "../../elements/GalleryModal/GalleryModal";
interface NoteItemProps {
  note: iNote;
  isOpen: boolean;
  onToggle: (id: string) => void;
  deviceOrFence: 'device' | 'fence';
  deviceOrFenceId: string;
  editLinkState: { state: { ownerTitle?: string } };
  disabled: boolean;
  noteFieldDefs?: Record<string, { details: { name: string; kind: string } }>;
  globalVisibleFields: string[];
  showDeviceName?: boolean;
  deviceName?: string;
}

const NoteItem = ({
  note,
  isOpen,
  onToggle,
  deviceOrFence,
  deviceOrFenceId,
  editLinkState,
  disabled,
  noteFieldDefs = {},
  globalVisibleFields,
  showDeviceName = false,
  deviceName = ''
}: NoteItemProps) => {
  const history = useHistory();
  const skipNextBodyClickRef = useRef(false);
  const rowClickTimeoutRef = useRef<number | null>(null);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [galleryFiles, setGalleryFiles] = useState<GalleryFile[]>([]);
  const [galleryIndex, setGalleryIndex] = useState(0);

  const getNormalizedFiles = () => {
    const rawFile = note.file;
    if (!rawFile) return [];

    if (Array.isArray(rawFile)) return rawFile;

    if (typeof rawFile === 'object') {
      if ('url' in rawFile) return [rawFile];

      return Object.values(rawFile);
    }

    return [];
  };

  const filesList = getNormalizedFiles();
  const openEdit = () => {
    history.push(editTo as any);
  };

  const handleRowClick = () => {
    if (rowClickTimeoutRef.current !== null) {
      window.clearTimeout(rowClickTimeoutRef.current);
    }

    rowClickTimeoutRef.current = window.setTimeout(() => {
      onToggle(note.id);
      rowClickTimeoutRef.current = null;
    }, 200);
  };

  const handleRowDoubleClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (disabled) return;

    if (rowClickTimeoutRef.current !== null) {
      window.clearTimeout(rowClickTimeoutRef.current);
      rowClickTimeoutRef.current = null;
    }

    e.preventDefault();
    openEdit();
  };

  const getExtraFieldValueByDisplayName = (displayName: string): string => {
    const fieldEntry = Object.entries(noteFieldDefs).find(([, def]) => {
      const name = def?.details?.name;
      return typeof name === 'string' && name.trim() === displayName.trim();
    });

    if (!fieldEntry) return '';

    const [fieldId] = fieldEntry;
    const rawValue = note.extraValues?.[fieldId]?.value;

    if (rawValue === null || rawValue === undefined || rawValue === '') return '';
    if (typeof rawValue === 'boolean') return rawValue ? 'Yes' : 'No';
    if (typeof rawValue === 'object' && rawValue !== null && 'url' in rawValue) {
      return (rawValue as any).fileName || 'Attachment';
    }
    return String(rawValue);
  };

  const getPartsVendorOrRepairShopValue = (): string => {
    const partsVendorValue = getExtraFieldValueByDisplayName('Parts Vendor');
    const repairShopValue = getExtraFieldValueByDisplayName('Repair Shop');
    return partsVendorValue || repairShopValue || '';
  };

  const getServiceOrPartValue = (): string => {
    const serviceValue = getExtraFieldValueByDisplayName('Service');
    const partValue = getExtraFieldValueByDisplayName('PART');
    return serviceValue || partValue || '';
  };

  const getFieldValueForFiltering = (fieldId: string): string => {
    switch (fieldId) {
      case 'date': return note.date ? moment(note.date).format(C.simpleDateFormat) : '';
      case 'part': return getPartsVendorOrRepairShopValue();
      case 'vendor': return getServiceOrPartValue();
      case 'note': return note.note || '';
      case 'po': return note.po || '';
      default:
        const extraValue = note.extraValues?.[fieldId]?.value;
        if (!extraValue) return '';
        if (typeof extraValue === 'object' && extraValue !== null && 'url' in extraValue) {
          return (extraValue as any).fileName || 'Attachment';
        }
        if (typeof extraValue === 'boolean') return extraValue ? 'Yes' : 'No';
        return String(extraValue);
    }
  };

  // Render only visible fields that have values, with 'note' always last
  const fieldsWithValues = globalVisibleFields
    .filter(field => field !== 'note')
    .filter(field => {

      const value = getFieldValueForFiltering(field);
      return value !== '';
    });

  const displayFields = [...fieldsWithValues, ...(globalVisibleFields.includes('note') ? ['note'] : [])];

  // Fields to show in the expanded accordion view
  const detailFields = [
    'techName', 'completedDate', 'amount', 'complete',
    ...Object.keys(note.extraValues || {})
  ];

  const editTo = {
    pathname: `/${deviceOrFence}/${deviceOrFenceId}/notes/edit/${note.id}`,
    ...editLinkState,
  };

  useEffect(() => {
    return () => {
      if (rowClickTimeoutRef.current !== null) {
        window.clearTimeout(rowClickTimeoutRef.current);
      }
    };
  }, []);

  const hasTextSelection = () => {
    const sel = window.getSelection?.();
    return !!sel && sel.type === 'Range' && sel.toString().length > 0;
  };

  const markSkipIfSelectedText = () => {
    if (!isOpen || disabled) return;

    if (hasTextSelection()) {
      skipNextBodyClickRef.current = true;
      window.setTimeout(() => {
        skipNextBodyClickRef.current = false;
      }, 0);
    }
  };

  const handleBodyClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (!isOpen) return;
    if (disabled) return;

    if (skipNextBodyClickRef.current || hasTextSelection()) {
      e.preventDefault();
      return;
    }

    openEdit();
  };

  const openGallery = (files: GalleryFile[], startIndex: number) => {
    setGalleryFiles(files);
    setGalleryIndex(startIndex);
    setIsGalleryOpen(true);
  };

  const formatFallbackFieldLabel = (fieldId: string) => {
    return fieldId
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (ch) => ch.toUpperCase());
  };

  const getFieldLabel = (fieldId: string) => {
    switch (fieldId) {
      case 'date': return 'Date';
      case 'partService': return 'PART/Service';
      case 'vendor': return 'Vendor/Repair Shop';
      case 'note': return 'Note';
      case 'po': return 'PO';
      case 'techName': return 'Tech Name';
      case 'completedDate': return 'Completed';
      case 'amount': return 'Amount';
      case 'complete': return 'Complete';
      case 'file': return 'Files';
      default:
        return noteFieldDefs[fieldId]?.details?.name || formatFallbackFieldLabel(fieldId);
    }
  };

  const hasDealershipRepairShopValue = (): boolean => {
    if (note.repairShop !== null && note.repairShop !== undefined && String(note.repairShop).trim() !== '') {
      return true;
    }

    const normalizedKey = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '');
    const target = 'dealershiprepairshop';

    const entries = Object.entries(note.extraValues || {});
    return entries.some(([fieldId, fieldValue]) => {
      const label = noteFieldDefs[fieldId]?.details?.name || '';
      const matchesByName = normalizedKey(label).includes(target);
      const matchesByKey = normalizedKey(fieldId).includes(target);
      if (!matchesByName && !matchesByKey) return false;

      const val = fieldValue?.value;
      return val !== null && val !== undefined && val !== '';
    });
  };

  const getFieldValue = (fieldId: string): React.ReactNode => {
    switch (fieldId) {
      case 'date': return note.date ? moment(note.date).format(C.simpleDateFormat) : '';
      case 'part': return getPartsVendorOrRepairShopValue();
      case 'vendor': return getServiceOrPartValue();
      case 'note': return note.note || '';
      case 'po': return note.po || '';
      case 'techName': return note.techName || '';
      case 'completedDate': return note.completedDate || '';
      case 'amount':
        if (note.amount != null && !isNaN(note.amount)) {
          return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(note.amount));
        }
        return '';
      case 'complete': return note.complete ? 'Yes' : 'No';
      default: {
        const extraValue = note.extraValues?.[fieldId];
        if (!extraValue) return '';
        const value = extraValue.value;
        if (value === null || value === undefined || value === '') return '';
        if (typeof value === 'object' && value !== null && 'url' in value) {
          const fileValue = value as { url: string; fileName: string; format?: 'pdf' | 'image' };
          const fileFormat = fileValue.format || (fileValue.url.toLowerCase().includes('.pdf') ? 'pdf' : 'image');
          return (
            <span
              style={{ color: "blue", cursor: "pointer", textDecoration: "underline" }}
              onClick={(e) => {
                e.stopPropagation();
                openGallery([{ ...fileValue, format: fileFormat }], 0);
              }}
            >
              {fileValue.fileName || 'Attachment'}
            </span>
          );
        }
        if (typeof value === 'boolean') return value ? 'Yes' : 'No';
        const fieldDef = noteFieldDefs[fieldId]?.details;
        if (
          fieldDef &&
          typeof fieldDef.kind === 'string' &&
          fieldDef.kind.toLowerCase() === 'number' &&
          (typeof value === 'number' || typeof value === 'string') &&
          !isNaN(Number(value))
        ) {
          return new Intl.NumberFormat('en-US').format(Number(value));
        }
        return String(value);
      }
    }
  };

  return (
    <div className={styles.customAccordionContainer}>
      <div
        className={`${styles.noteContainer} ${disabled ? styles.disabled : ''} ${hasDealershipRepairShopValue() ? styles.dealershipRepairShopRow : ''}`}
        onClick={handleRowClick}
        onDoubleClick={handleRowDoubleClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle(note.id);
          }
        }}
      >
        {displayFields.map((fieldId) => {
          if (fieldId === 'note') return null;
          // skip file-type extra fields in the collapsed row — shown via paperclip instead
          const extraVal = note.extraValues?.[fieldId]?.value;
          if (extraVal && typeof extraVal === 'object' && 'url' in extraVal) return null;
          const className = styles[`note${fieldId.charAt(0).toUpperCase() + fieldId.slice(1)}`] || styles.noteText;

          return (
            <span
              key={fieldId}
              className={className}
            >
              {getFieldValue(fieldId)}
            </span>
          );
        })}

        {filesList.length > 0 && (
          <span className={styles.notePaperclip}>
            <Fa icon={faPaperclip} />
          </span>
        )}

        {displayFields.includes('note') && (
          <span className={styles.noteNote}>
            {getFieldValue('note')}
          </span>
        )}

        {showDeviceName && (
          <span className={styles.noteDeviceName} title={deviceName || ''}>
            {deviceName || '-'}
          </span>
        )}
      </div>

      <div
        className={`${styles.accordionContent} ${isOpen ? styles.open : ''}`}
        onMouseDown={() => { skipNextBodyClickRef.current = false; }}
        onMouseUp={markSkipIfSelectedText}
        onDoubleClick={markSkipIfSelectedText}
        onClick={handleBodyClick}
        style={!disabled && isOpen ? { cursor: 'pointer' } : undefined}
        title={!disabled && isOpen ? 'Click to edit' : undefined}
        role={!disabled && isOpen ? "button" : undefined}
        tabIndex={!disabled && isOpen ? 0 : -1}
        onKeyDown={(e) => {
          if (!disabled && isOpen && (e.key === 'Enter' || e.key === ' ')) {
            if (hasTextSelection()) return;
            e.preventDefault();
            history.push(editTo as any);
          }
        }}
      >
        {note.note && (
          <div style={{ cursor: 'text' }}>
            <strong>Note:</strong> {note.note}
          </div>
        )}

        {detailFields.map(fieldId => {
          if (!['techName', 'completedDate', 'amount', 'complete'].includes(fieldId) && !note.extraValues?.[fieldId]) return null;

          const value = getFieldValue(fieldId);
          if (!value) return null;

          return (
            <div key={fieldId} style={{ cursor: 'text' }}>
              <strong>{getFieldLabel(fieldId)}:</strong> {value}
            </div>
          );
        })}

        {filesList.length > 0 && (
          <div style={{ marginTop: 5, cursor: 'text' }}>
              <strong>Attachments:</strong>{' '}
              <div className={styles.filesGrid}>
              {filesList.map((f: any, index) => (
                <React.Fragment key={index}>
                  {f.format === 'image' ? (
                    <span>
                      <img
                        src={f.url}
                        alt={f.fileName}
                        className={styles.previewImage}
                        style={{ maxWidth: '100px', maxHeight: '100px', cursor: 'pointer' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          openGallery(filesList as GalleryFile[], index);
                        }}
                      />
                    </span>
                  ) : (
                    <span
                      style={{ cursor: "pointer", color: "blue", textDecoration: "underline", marginLeft: index === 0 ? '0' : '5px' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        openGallery(filesList as GalleryFile[], index);
                      }}
                    >
                      {f.fileName || `Document ${index + 1}`}
                    </span>
                  )}
                  {index < filesList.length - 1 && f.format !== 'image' && ', '}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {isOpen && (
          <button
            type="button"
            className={styles.minimizeButton}
            onClick={(e) => {
              e.stopPropagation();
              onToggle(note.id);
            }}
            aria-label="Minimize note"
            title="Minimize"
          >
            X
          </button>
        )}
      </div>

      {isGalleryOpen && (
        <GalleryModal
          open={isGalleryOpen}
          files={galleryFiles}
          initialIndex={galleryIndex}
          onClose={() => setIsGalleryOpen(false)}
        />
      )}
    </div>
  );
};

NoteItem.displayName = 'NoteItem';

export default NoteItem;